import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { ShowsService } from '../../services/shows/shows.service';
import { SessionsService } from '../../services/sessions/sessions.service';

import * as dayjs from 'dayjs';
import * as dayjsIsToday from 'dayjs/plugin/isToday';
import * as dayjsIsTomorrow from 'dayjs/plugin/isTomorrow';
import { Subscription } from 'rxjs';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { PopoverController } from '@ionic/angular';
import { MoveSessionPage } from '../../modals/move-session/move-session.page';
import { ShowSelectMenuComponent } from '../../menus/show-select-menu/show-select-menu.component';
import { LafayetteService } from '../../services/lafayette/lafayette.service';
import { ToastrService } from 'ngx-toastr';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { RollbarService } from '../../services/rollbar/rollbar.service';
import Rollbar from 'rollbar';

@Component({
  selector: 'sc-show-session-label',
  templateUrl: './show-session-label.component.html',
  styleUrls: ['./show-session-label.component.scss'],
})
export class ShowSessionLabelComponent implements OnInit, OnDestroy {
  studioShow$ = this.sessionsService.studioShow$;
  studioSession$ = this.sessionsService.studioSession$;
  availableShows$ = this.showsService.availableShows$;
  rawDate: number;
  formattedStart: string;
  formattedLocal: string;
  formattedEnd: string;

  subs: Array<Subscription> = [];

  constructor(
    private analyticsService: AnalyticsService,
    private lafayetteService: LafayetteService,
    private popoverController: PopoverController,
    private showsService: ShowsService,
    private sessionsService: SessionsService,
    private toastrService: ToastrService,
    @Inject(RollbarService) private rollbar: Rollbar
  ) {}

  ngOnInit() {
    dayjs.extend(dayjsIsToday);
    dayjs.extend(dayjsIsTomorrow);
    this.setupSessionDate();
  }

  async openShowSelectMenu(event: MouseEvent) {
    const oldShowID = this.studioSession$.value.showID;
    let target = event.target as HTMLElement;
    while (target.nodeName !== 'ION-ITEM') {
      target = target.parentElement;
    }
    const newEvent = { ...event, target };
    this.analyticsService.track('opened move session in greenroom', {
      sessionID: this.studioSession$.value.sessionID,
    });

    const popover = await this.popoverController.create({
      component: ShowSelectMenuComponent,
      cssClass: 'show-select-popover',
      side: 'bottom',
      alignment: 'start',
      size: 'cover',
      showBackdrop: false,
      event: newEvent,
      componentProps: { selectedShowID: this.studioSession$.value.showID },
    });
    await popover.present();
    const resp = await popover.onDidDismiss();

    if (resp.data?.showID) {
      try {
        await this.lafayetteService.moveSession(this.studioSession$.value.sessionID, resp.data.showID);
        window.history.replaceState(null, '', window.location.href.replace(oldShowID, resp.data.showID));
        this.analyticsService.track('moved session in greenroom', {
          sessionID: this.studioSession$.value.sessionID,
          movedToShowID: resp.data.showID,
          success: true,
        });
      } catch (error) {
        this.toastrService.error('An error occurred switching shows.', 'Failed to Move Session', {
          toastComponent: GeneralToastComponent,
          progressBar: true,
          closeButton: true,
        });

        this.rollbar.error('Failed to move session to a different show', {
          error,
          sessionID: this.studioSession$.value.sessionID,
          showID: resp.data.showID,
        });

        this.analyticsService.track('moved session in greenroom', {
          sessionID: this.studioSession$.value.sessionID,
          movedToShowID: resp.data.showID,
          success: false,
        });
      }
    }
    popover.dismiss();
  }

  setupSessionDate() {
    const sub = this.studioSession$.subscribe((session) => {
      if (!session?.startTimestamp) return;
      if (session.startTimestamp !== this.rawDate) {
        this.rawDate = session.startTimestamp;
        this.formattedStart = this.parseDate(session.startTimestamp);
        this.formattedLocal = this.parseDate(session.startTimestamp, true);
        this.formattedEnd = this.parseDate(session.endTime, false, true);
      }
    });
    this.subs.push(sub);
  }

  parseDate(date, local?: boolean, endTime?: boolean) {
    let sessionDate;
    let formattedDate;
    let formattedTime;

    if (!endTime) {
      sessionDate = dayjs.unix(date);
      formattedDate = dayjs(sessionDate).format('MM/DD/YYYY');
      formattedTime = dayjs(sessionDate).format('h:mm a');
      if (this.studioSession$.value?.timezone && !local) {
        const sessionTimezone = this.studioSession$.value.timezone.tzid;
        formattedDate = dayjs(sessionDate).tz(sessionTimezone).format('MM/DD/YYYY');
        formattedTime = dayjs(sessionDate).tz(sessionTimezone).format('h:mm a z');
      } else if (this.studioSession$.value?.timezone && local) {
        formattedDate = dayjs(sessionDate).format('MM/DD/YYYY');
        formattedTime = dayjs(sessionDate).format('h:mm a z');
      }

      const isToday = dayjs(sessionDate).isToday();
      const isTomorrow = dayjs(sessionDate).isTomorrow();

      return `${isToday ? 'Today' : isTomorrow ? 'Tomorrow' : formattedDate} ● ${formattedTime}`;
    } else {
      if (date?.includes('PM') || date?.includes('AM')) {
        return date;
      } else {
        sessionDate = dayjs(date);
        formattedTime = dayjs(sessionDate).format('h:mm a');
        if (this.studioSession$.value?.timezone && !local) {
          const sessionTimezone = this.studioSession$.value.timezone.tzid;
          formattedTime = dayjs(sessionDate).tz(sessionTimezone).format('h:mm a z');
        } else if (this.studioSession$.value?.timezone && local) {
          formattedTime = dayjs(sessionDate).format('h:mm a z');
        }
        return formattedTime;
      }
    }
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
